import {
  flexRender,
  Row,
  type Table as TanstackTable,
} from '@tanstack/react-table';
import type * as React from 'react';

import { getCommonPinningStyles } from '@app/components/data-table/lib/data-table';
import { DataTableSkeletonRows } from '@shared/components/data-table/data-table-skeleton-rows';
import {
  ColumnClassName,
  DataTableHeaderProps,
} from '@shared/components/data-table/types';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@shared/components/ui/table';
import { cn } from '@shared/lib/utils';
import { ClassValue } from 'clsx';
import { DataTablePagination } from './data-table-pagination';
import { DataTableSkeleton } from './data-table-skeleton';
import {
  DataTableContext,
  DataTableContextProps,
} from './hooks/use-server-data-table';

interface DataTableProps<TData> extends React.ComponentProps<'div'> {
  table: TanstackTable<TData>;
  actionBar?: React.ReactNode;
  onRowClick?: (row?: Row<TData>) => void;
  onRowMouseEnter?: (row: Row<TData>) => void;
  onRowMouseLeave?: (row: Row<TData>) => void;
  rowClassName?: (row: Row<TData>) => ClassValue;
  cellClassName?: ClassValue;
  columnClassName?: ColumnClassName;
  headerConfig?: DataTableHeaderProps;
  context?: DataTableContextProps<TData>;
  tableLoading?: boolean;
  isLoading?: boolean;
  showHeader?: boolean;
  customRowItem?: (row: Row<TData>) => React.ReactNode;
  rowClickColumnDisabledIds?: (row: Row<TData>) => string[];
  pagination?: boolean;
  tableClassName?: string;
}

export function DataTable<TData>({
  table,
  actionBar,
  children,
  className,
  rowClassName,
  cellClassName,
  headerConfig,
  columnClassName = {},
  onRowClick,
  onRowMouseEnter,
  onRowMouseLeave,
  context,
  isLoading,
  tableLoading,
  showHeader = true,
  customRowItem,
  rowClickColumnDisabledIds = () => [],
  pagination = true,
  tableClassName,
  ...props
}: DataTableProps<TData>) {
  const hasRows = table.getRowModel().rows?.length > 0;
  const pageSize = table.getState().pagination.pageSize || 10;
  const dataLength = table.getPreFilteredRowModel().rows.length;

  const columnFilters = table
    .getAllColumns()
    .filter((column) => column.columnDef.enableColumnFilter);

  if (tableLoading) {
    return (
      <DataTableSkeleton
        table={table}
        columnCount={table.getVisibleFlatColumns().length}
        rowCount={pageSize || 10}
        filterCount={columnFilters.length}
        withViewOptions={false}
      >
        {children}
      </DataTableSkeleton>
    );
  }

  return (
    <DataTableContext.Provider value={context!}>
      <div
        className={cn('flex w-full flex-col gap-2.5 overflow-auto')}
        {...props}
      >
        {children}
        <div className={cn('rounded-lg', className)}>
          <Table className={cn('relative overflow-hidden', tableClassName)}>
            {showHeader && (
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        colSpan={header.colSpan}
                        style={{
                          ...getCommonPinningStyles({ column: header.column }),
                        }}
                        className={cn(headerConfig?.cell, '', {
                          [columnClassName[header.column.id] as string]:
                            columnClassName[header.column.id] || '',
                        })}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
            )}

            <TableBody>
              {isLoading && (
                <DataTableSkeletonRows
                  table={table}
                  rowCount={dataLength || pageSize}
                />
              )}
              {!isLoading &&
                hasRows &&
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    onMouseEnter={
                      onRowMouseEnter && (() => onRowMouseEnter(row))
                    }
                    onMouseLeave={
                      onRowMouseLeave && (() => onRowMouseLeave(row))
                    }
                    className={cn(
                      'bg-none',
                      onRowClick && 'cursor-pointer',
                      rowClassName && rowClassName(row)
                    )}
                  >
                    {customRowItem ? (
                      <>{customRowItem(row)}</>
                    ) : (
                      <>
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            key={cell.id}
                            className={cn(
                              '',
                              {
                                [columnClassName[cell.column.id] as string]:
                                  columnClassName[cell.column.id] || '',
                              },
                              cellClassName
                            )}
                            style={{
                              ...getCommonPinningStyles({
                                column: cell.column,
                              }),
                            }}
                            onClick={() => {
                              cell.column.id !== 'actions' &&
                                cell.column.id !== 'select' &&
                                !rowClickColumnDisabledIds(row).includes(
                                  cell.column.id
                                ) &&
                                onRowClick &&
                                onRowClick(row);
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </>
                    )}
                  </TableRow>
                ))}
              {!isLoading && !hasRows && (
                <TableRow>
                  <TableCell
                    colSpan={table.getAllColumns().length}
                    className="h-24 text-center"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex flex-col gap-2.5">
          {pagination && <DataTablePagination table={table} />}
          {actionBar &&
            Object.keys(table.getState().rowSelection).length > 0 &&
            actionBar}
        </div>
      </div>
    </DataTableContext.Provider>
  );
}
